/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import type { ReactNode } from 'react';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { cssMap, jsx } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';

import { CopyResponse } from './copy-response/CopyResponse';
import { DebugButton } from './debug-button/DebugButton';
import { DislikeAnswer } from './like-answer/DislikeAnswer';
import { LikeAnswer } from './like-answer/LikeAnswer';
import { type MessageFeedbackType } from './types';

const styles = cssMap({
	messageFooterButtonUnorderedList: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		listStyle: 'none !important', // important is needed to override editor styles that supercede this
		padding: 0,
		margin: 0,
	},
	listItem: {
		margin: 0,
	},
});

export type MessageFooterButtonsProps = {
	onThumbClick: (value: boolean, event: UIAnalyticsEvent) => void;
	hideThumbs: boolean;
	onCopyClick: (event: UIAnalyticsEvent) => void;
	showDebugButton?: boolean;
	onClickDebug?: (event: UIAnalyticsEvent) => void;
	feedbackType?: MessageFeedbackType | null;
	/** Test id forwarded to both thumb buttons (e.g. Pollinator selector). */
	thumbsTestId?: string;
	/** Trailing list item slot — e.g. ai-mate InsertReplaceButton. Caller gates visibility. */
	insertSlot?: ReactNode;
	/**
	 * Optional list item rendered immediately after the copy button — e.g. the
	 * ai-mate "share chat up to this point" button. Caller gates visibility; when omitted
	 * nothing is rendered, preserving the previous layout.
	 */
	shareSlot?: ReactNode;
};

export const MessageFooterButtons = ({
	hideThumbs,
	onThumbClick,
	onCopyClick,
	showDebugButton = false,
	onClickDebug,
	feedbackType,
	thumbsTestId,
	insertSlot,
	shareSlot,
}: MessageFooterButtonsProps): JSX.Element => {
	return (
		<Inline
			alignInline="start"
			alignBlock="center"
			space="space.050"
			as="ul"
			xcss={styles.messageFooterButtonUnorderedList}
		>
			{!hideThumbs && (
				<>
					<Box as="li" xcss={styles.listItem}>
						<LikeAnswer
							onClick={(event) => onThumbClick(true, event)}
							isSelected={feedbackType === 'like'}
							spacing="default"
							testId={thumbsTestId}
						/>
					</Box>
					<Box as="li" xcss={styles.listItem}>
						<DislikeAnswer
							onClick={(event) => onThumbClick(false, event)}
							isSelected={feedbackType === 'dislike'}
							spacing="default"
							testId={thumbsTestId}
						/>
					</Box>
				</>
			)}
			<Box as="li" xcss={styles.listItem}>
				<CopyResponse onClick={(event) => onCopyClick(event)} spacing="default" />
			</Box>
			{shareSlot && (
				<Box as="li" xcss={styles.listItem}>
					{shareSlot}
				</Box>
			)}
			{showDebugButton && (
				<Box as="li" xcss={styles.listItem}>
					<DebugButton onClick={(event) => onClickDebug?.(event)} />
				</Box>
			)}
			{insertSlot && (
				<Box as="li" xcss={styles.listItem}>
					{insertSlot}
				</Box>
			)}
		</Inline>
	);
};
